/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CustomAppGeneralTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Settings, Code } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import { getDefaultScript } from '../utils/customAppDefaults';

interface CustomAppGeneralTabProps {
    name: string;
    description: string;
    appType: 'lua' | 'xml';
    scriptContent: string;
    enabled: boolean;
    skipFinalDestination: boolean;
    finalDestinationModule: string;
    finalDestinationValue: string;
    onNameChange: (v: string) => void;
    onDescriptionChange: (v: string) => void;
    onAppTypeChange: (v: 'lua' | 'xml') => void;
    onScriptContentChange: (v: string) => void;
    onEnabledChange: (v: boolean) => void;
    onSkipFinalDestinationChange: (v: boolean) => void;
    onFinalDestinationModuleChange: (v: string) => void;
    onFinalDestinationValueChange: (v: string) => void;
    t: (key: string) => string;
}

export function CustomAppGeneralTab({
    name, description, appType, scriptContent, enabled,
    skipFinalDestination, finalDestinationModule, finalDestinationValue,
    onNameChange, onDescriptionChange, onAppTypeChange, onScriptContentChange,
    onEnabledChange, onSkipFinalDestinationChange,
    onFinalDestinationModuleChange, onFinalDestinationValueChange, t
}: CustomAppGeneralTabProps) {
    return (
        <div className="space-y-4">
            {/* Basic Settings */}
            <FormBox title={t('customApplications.sections.basic')} icon={<Settings className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Name */}
                    <SimpleTooltip content={t('customApplications.tooltips.name')} title={t('customApplications.form.name')} position="top" maxWidth="300px">
                        <Label htmlFor="name" required className="text-left">{t('customApplications.form.name')}</Label>
                    </SimpleTooltip>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        placeholder={t('customApplications.form.namePlaceholder')}
                    />

                    {/* Description */}
                    <SimpleTooltip content={t('customApplications.tooltips.description')} title={t('customApplications.form.description')} position="top" maxWidth="300px">
                        <Label htmlFor="description" className="text-left">{t('customApplications.form.description')}</Label>
                    </SimpleTooltip>
                    <Input
                        id="description"
                        value={description}
                        onChange={(e) => onDescriptionChange(e.target.value)}
                        placeholder={t('customApplications.form.descriptionPlaceholder')}
                    />

                    {/* App Type */}
                    <SimpleTooltip content={t('customApplications.tooltips.appType')} title={t('customApplications.form.appType')} position="top" maxWidth="300px">
                        <Label htmlFor="appType" className="text-left">{t('customApplications.form.appType')}</Label>
                    </SimpleTooltip>
                    <select
                        id="appType"
                        value={appType}
                        onChange={(e) => {
                            const newType = e.target.value as 'lua' | 'xml';
                            onAppTypeChange(newType);
                            if (!scriptContent.trim()) {
                                onScriptContentChange(getDefaultScript(newType));
                            }
                        }}
                        className="h-10 rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="lua">{t('customApplications.form.appTypeLua')}</option>
                        <option value="xml">{t('customApplications.form.appTypeXml')}</option>
                    </select>

                    {/* Enabled */}
                    <SimpleTooltip content={t('customApplications.tooltips.enabled')} title={t('customApplications.form.enabled')} position="top" maxWidth="300px">
                        <Label htmlFor="enabled" className="text-left">{t('customApplications.form.enabled')}</Label>
                    </SimpleTooltip>
                    <Toggle value={enabled} onChange={onEnabledChange} />
                </div>
            </FormBox>

            {/* Destination */}
            <FormBox title={t('customApplications.sections.destination')} icon={<Code className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Skip Final Destination */}
                    <SimpleTooltip content={t('customApplications.tooltips.skipFinalDestination')} title={t('customApplications.form.skipFinalDestination')} position="top" maxWidth="300px">
                        <Label htmlFor="skipFinalDestination" className="text-left">{t('customApplications.form.skipFinalDestination')}</Label>
                    </SimpleTooltip>
                    <Toggle value={skipFinalDestination} onChange={onSkipFinalDestinationChange} />
                    <div /> {/* Empty cell */}
                    <div /> {/* Empty cell */}

                    {/* Final Destination */}
                    {!skipFinalDestination && (
                        <>
                            <SimpleTooltip content={t('customApplications.tooltips.finalDestination')} title={t('customApplications.form.finalDestination')} position="top" maxWidth="300px">
                                <Label htmlFor="finalDestination" className="text-left">{t('customApplications.form.finalDestination')}</Label>
                            </SimpleTooltip>
                            <div className="col-span-3">
                                <ModuleDestinationSelect
                                    moduleValue={finalDestinationModule}
                                    destinationValue={finalDestinationValue}
                                    onModuleChange={onFinalDestinationModuleChange}
                                    onDestinationChange={onFinalDestinationValueChange}
                                    modulePlaceholder={t('customApplications.form.selectModule')}
                                    destinationPlaceholder={t('customApplications.form.selectDestination')}
                                />
                            </div>
                        </>
                    )}
                </div>
            </FormBox>
        </div>
    );
}
